/**
 * LegalView — renders a static legal doc (privacy / terms / refund) at a public
 * route. Source markdown is imported via Vite ?raw (zero runtime fetch) and
 * rendered by <Markdown>. Minimal chrome: wordmark + back link + Footer.
 *
 * ponytail: one component switches on pathname rather than 3 near-identical
 * pages; markdown lives in client/src/content/legal/*.md so it edits as text.
 */

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import privacyMd from '../../content/legal/privacy.md?raw'
import termsMd from '../../content/legal/terms.md?raw'
import refundMd from '../../content/legal/refund.md?raw'
import { Markdown } from '../Markdown'
import { Footer } from '../landing/Footer'
import { LandingI18nProvider } from '../landing/LandingI18n'
import { useLandingT, usePublicHref } from '../landing/landing-locale'
import { splitLocalePath } from '../../lib/public-locale'

const DOCS: Record<string, { source: string; title: string }> = {
  '/privacy': { source: privacyMd, title: 'Privacy Policy — Piccard' },
  '/terms': { source: termsMd, title: 'Terms of Service — Piccard' },
  '/refund': { source: refundMd, title: 'Refund Policy — Piccard' },
}

export function LegalView() {
  const { pathname } = useLocation()
  // Normalize trailing slash: Workers static assets serves dist/terms/index.html
  // as a directory, redirecting /terms → /terms/, so pathname can be '/terms/'.
  // Strip any locale prefix first — seo plan M2.
  const normalized = splitLocalePath(pathname).path.replace(/\/$/, '') || '/'
  const doc = DOCS[normalized]

  useEffect(() => {
    if (doc) document.title = doc.title
  }, [doc])

  if (!doc) return null

  return (
    <LandingI18nProvider>
      <LegalViewBody doc={doc} />
    </LandingI18nProvider>
  )
}

function LegalViewBody({ doc }: { doc: { source: string; title: string } }) {
  // Same string as the blog back link — one surface, not per-view keys.
  const t = useLandingT()
  const href = usePublicHref()

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="border-b border-outline-variant">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href={href('/')} className="font-headline font-extrabold text-lg text-primary">
            Piccard
          </a>
          <a href={href('/')} className="text-sm text-muted-soft hover:text-on-surface transition-colors">
            {t('landing.blog.backHome')}
          </a>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <Markdown source={doc.source} />
      </main>

      <Footer />
    </div>
  )
}
